import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ComponentWithErrorModal } from '../../base/component-with-error-modal';
import { NotificationService, ReportRepositoryService, SessionService, TpSafeService } from '../../../services';
import { ModalService, ConfirmationModalComponent } from '../../../modules/modal';
import { EditReportTitleModalComponent } from './edit-report-title-modal/edit-report-title-modal.component';
import { Report, ReportInstance, Container, Message } from '../../../models';
import { ReportGenerationConstants } from '../../../constants/report-generation';
import { MessageService } from '../../../../shared/services';
import { ReportFileTypesConstants, TranslationConstants } from '../../../../shared/constants';
import { OrgChartSummaryModalComponent } from './org-chart-summary-modal/org-chart-summary-modal.component';

@Component({
  selector: 'working-report-repository',
  templateUrl: './working-report-repository.component.html',
  styleUrls: ['./working-report-repository.component.scss']
})
export class WorkingReportRepositoryComponent extends ComponentWithErrorModal implements OnInit {

  @Input() reportResourceRoute: string;
  @Input() finalRepositoryRoute: string;
  @Input() finalRepoRouteParams: any;
  @Input() repositoryTranlsatedTitle: string;
  @Input() repositoryNonTranlsatedTitle: string;

  @Input() backToName: string;
  @Input() repositoryName: string;
  @Input() backToLink: string;
  @Input() reports: Report[];
  @Input() createModalComponent: Component;
  @Input() createModalPayload: any;
  @Input() uploadModalComponent: Component;
  @Input() uploadModalPayload: any;
  @Output() refresh = new EventEmitter();
  @Output() createReport = new EventEmitter();
  @Input() isMasterFile: boolean;

  loadingReportInstances: boolean = false;
  reportInstances: ReportInstance[] = [];
  currentOpenReportId: number | null;
  pendingReportsTimeout: NodeJS.Timer;
  getReportInstancesTimeout: NodeJS.Timer;
  subscriptions: Subscription[] = [];
  workingContainer: Container;
  hasFinalVersion: boolean = false;
  hasPrimaryEntity: boolean;
  REPORT_STATUS_MAP = ReportGenerationConstants.REPORT_STATUS_BY_NUMBER;
  pendingReportInstanceIds: number[] = [];
  isNotIframe: boolean;
  queryParams: any;
  constructor(
    protected notificationsService: NotificationService,
    protected modalService: ModalService,
    private _router: Router,
    private _reportRepoService: ReportRepositoryService,
    private _sessionService: SessionService,
    private _tpSafeService: TpSafeService,
    private _translateService: TranslateService,
    private _messageService: MessageService,
  ) {
    super(notificationsService, modalService);
    const urlParams = new URLSearchParams(window.location.search);
    this.isNotIframe = !urlParams.get('iframe') || urlParams.get('iframe') !== 'true';
    this.queryParams = {
      iframe: urlParams.get('iframe')
    };
  }

  ngOnInit(): void {
    this.workingContainer = this._sessionService.getWorkingContainer();
    this._reportRepoService.setReportResourceRoute(this.reportResourceRoute);
  }

  ngOnDestroy(): void {
    this._reportRepoService.setReportResourceRoute(null);

    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });

    clearTimeout(this.pendingReportsTimeout);
    clearTimeout(this.getReportInstancesTimeout);
  }

  openCreateReportModal(): void {
    this.clearErrorMessages();
    this.isModalOpen = true;
    this.modalService.openModal({
      component: this.createModalComponent,
      success: () => {
        this.isModalOpen = false;
        this.clearErrorMessages();
        this.refresh.emit();
      },
      data: this.createModalPayload
    });
  }

  openVersionModal(report: Report): void {
    this.subscriptions.push(this._reportRepoService.listReportInstances(report.reportId)
      .subscribe((versionsHistory) => {
        let latestGeneratedVersion: ReportInstance;
        if (versionsHistory && versionsHistory.length) {
          latestGeneratedVersion = versionsHistory.sort((first, second) => (first.version < second.version) ? 1 : -1)[0];
        }

        this.clearErrorMessages();
        this.isModalOpen = true;
        this.modalService.openModal({
          component: this.createModalComponent,
          success: () => {
            this.isModalOpen = false;
            this.clearErrorMessages();
            this.getReportInstances(report.reportId);
          },
          data: {
            ...this.createModalPayload,
            report,
            reportInstanceId: latestGeneratedVersion.reportInstanceId,
            isNewVersion: true
          },
        });
      }));
  }

  openUploadReportVersionModal(report: Report): void {
    this.subscriptions.push(this._reportRepoService.listReportInstances(report.reportId)
      .subscribe((versionsHistory) => {
        let latestGeneratedVersion: ReportInstance;
        if (versionsHistory && versionsHistory.length) {
          latestGeneratedVersion = versionsHistory.sort((first, second) => (first.version < second.version) ? 1 : -1)[0];
        }

        this.clearErrorMessages();

        this.isModalOpen = true;
        this.modalService.openModal({
          component: this.uploadModalComponent,
          data: {
            ...this.uploadModalPayload,
            report,
            reportInstanceId: latestGeneratedVersion.reportInstanceId
          },
          success: () => {
            this.isModalOpen = false;
            this.clearErrorMessages();
            this.getReportInstancesAfterADelay(report.reportId);
          },
        });
      }));
  }

  openEditReportTitleModal(report: Report): void {
    this.clearErrorMessages();

    this.isModalOpen = true;
    this.modalService.openModal({
      component: EditReportTitleModalComponent,
      data: report,
      success: () => {
        this.clearErrorMessages();
        this.isModalOpen = false;
        this.refresh.emit();
      },
    });
  }

  openOrgChartSummaryModal(report: Report): void {
    this.clearErrorMessages();

    this.isModalOpen = true;
    this.modalService.openModal({
      component: OrgChartSummaryModalComponent,
      data: report,
      success: () => {
        this.clearErrorMessages();
        this.isModalOpen = false;
        this.refresh.emit();
      },
    });
  }

  goToFinalRepository(): void {
    this._router.navigate([`${this.finalRepositoryRoute}`], { queryParams: this.finalRepoRouteParams });
  }

  deleteReport(reportInstanceId: number, reportName: string): void {

    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.WORKING_REPORT_REPOSITORY_DELETE_REPORT,
        text: `${reportName}`,
        content: TranslationConstants.WORKING_REPORT_REPOSITORY_DELETE_REPORT_CONFIRMATION_TEXT
      } as Message,
      success: () => {
        this.subscriptions.push(
          this._reportRepoService.deleteReport(reportInstanceId).subscribe(() => {
            this.refresh.emit();
          })
        );
      },
    });
  }

  // this is needed as uploaded reports take some time to show in the DB
  private getReportInstancesAfterADelay(reportID: number): void {
    this.getReportInstancesTimeout = setTimeout(() => {
      this.getReportInstances(reportID);
    }, 2000);
  }

  private getReportInstances(reportID: number): void {
    if (reportID !== this.currentOpenReportId) {
      return;
    }

    this.loadingReportInstances = true;
    this.subscriptions.push(this._reportRepoService.listReportInstances(reportID).subscribe((data: ReportInstance[]) => {
      this.loadingReportInstances = false;
      this.reportInstances = data;
      this.checkForFinalReport(data);
      this.reportInstances.map((file) => {
        file.createdAt = moment(file.createdAt).format('LLLL');
        file.isReportNotReady = this.isReportStillPendingOrConverting(file);
        file.statusTooltipText = this.statusTooltipText(file);
        file.hasReportFailed = this.hasReportFailed(file);
        return file;
      });

      this.hasPrimaryEntity = !!this.reportInstances[0] && !!this.reportInstances[0].primaryEntities && !!this.reportInstances[0].primaryEntities.length;

      this.reportInstances.sort((file1: ReportInstance, file2: ReportInstance) => file1.version - file2.version);

      if (this.hasPendingOrConvertingReports(this.reportInstances)) {
        this.pendingReportsTimeout = setTimeout(() => this.getReportInstances(reportID), 30000);
      }
    }));
  }

  hasPendingOrConvertingReports(reportInstances: ReportInstance[]): boolean {
    this.checkFinishedPendingReport(reportInstances);
    const inProcessStatuses = [ReportGenerationConstants.REPORT_STATUS_BY_NAME.PENDING, ReportGenerationConstants.REPORT_STATUS_BY_NAME.CONVERTING];
    reportInstances.forEach(reportInstance => {
      if (inProcessStatuses.includes(reportInstance.status)) {
        this.pendingReportInstanceIds.push(reportInstance.reportInstanceId);
      }
    });
    return reportInstances.some(file => inProcessStatuses.includes(file.status));
  }

  checkFinishedPendingReport(reportInstances: ReportInstance[]) {
    const successStatus = [ReportGenerationConstants.REPORT_STATUS_BY_NAME.GENERATED, ReportGenerationConstants.REPORT_STATUS_BY_NAME.UPLOADED];
    const failedStatus = ReportGenerationConstants.REPORT_STATUS_BY_NAME.FAILED;
    this.pendingReportInstanceIds.forEach(pendingReportInstanceId => {
      const pendingReportInstance = reportInstances.find(reportInstance => reportInstance.reportInstanceId === pendingReportInstanceId);
      if (successStatus.includes(pendingReportInstance.status)) {
        this._messageService.displayMessage({ type: 'success', body: pendingReportInstance.statusTooltipText });
      }
      if (failedStatus === pendingReportInstance.status) {
        this._messageService.displayMessage({ type: 'error', body: pendingReportInstance.statusTooltipText });
      }
    });
    this.pendingReportInstanceIds = [];
  }

  toggleCollapseAndGetInstances(report: Report): void {
    if (report.collapsed) {
      this.collapseAll();
      report.collapsed = false;
      this.hasFinalVersion = false;
      this.currentOpenReportId = report.reportId;
      this.getReportInstances(report.reportId);
    } else {
      this.currentOpenReportId = null;
      report.collapsed = true;
    }
  }

  collapseAll() {
    this.reports.map(report => report.collapsed = true);
  }

  statusTooltipText(reportInstance: ReportInstance): string {
    const status = reportInstance.status;
    if (status === ReportGenerationConstants.REPORT_STATUS_BY_NAME.FAILED && reportInstance.errorType) {
      return this._translateService.instant(reportInstance.errorType);
    }
    return ReportGenerationConstants.TOOLTIP_TEXT_BY_NUMBER[status] || '';
  }

  isReportStillPendingOrConverting(reportInstance: ReportInstance): boolean {
    const status = reportInstance.status;
    const inProcessStatuses = [ReportGenerationConstants.REPORT_STATUS_BY_NAME.PENDING, ReportGenerationConstants.REPORT_STATUS_BY_NAME.CONVERTING];
    const isPendingOrConverting = inProcessStatuses.includes(status);
    return isPendingOrConverting;
  }

  deleteReportInstance(reportId: number, instanceId: number, reportName: string, reportVersion: number): void {
    let updateActiveStatusReport;
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.WORKING_REPORT_REPOSITORY_DELETE_REPORT_VERSION,
        text: `${reportName} - ${reportVersion}`,
        content: TranslationConstants.WORKING_REPORT_REPOSITORY_DELETE_REPORT_VERSION_CONFIRMATION_TEXT
      } as Message,
      success: () => {
        const currentReport = this.reportInstances.find(report => report.reportInstanceId === instanceId);
        if (currentReport.isActive) {
          this.reportInstances.forEach((instance) => {
            if ((updateActiveStatusReport === undefined || new Date(instance.createdAt) > new Date(updateActiveStatusReport.createdAt)) && instance.reportInstanceId !== instanceId) {
              updateActiveStatusReport = instance;
            }
          });
        }

        if (updateActiveStatusReport) {
          this.subscriptions.push(this._reportRepoService.updateRepostActiveStatus(reportId, updateActiveStatusReport.reportInstanceId, this.workingContainer.containerId).subscribe(()=>{}));
        }
        this.subscriptions.push(
          this._reportRepoService.deleteReportInstance(reportId, instanceId).subscribe(() => {
            this.getReportInstances(reportId);
          })
        );
      },
    });

  }

  unpublishReport(reportId: number, instanceId: number): void {
    this.subscriptions.push(
      this._reportRepoService.unpublishReport(reportId, instanceId).subscribe(() => {
        this._reportRepoService.updateFinalFlag(reportId, instanceId, false, false).subscribe(() => {
          this.getReportInstances(reportId);
        });
      })
    );
  }

  publishReport(reportId: number, instanceId: number): void {
    this.subscriptions.push(
      this._reportRepoService.publishReport(reportId, instanceId, this.workingContainer.containerId).subscribe(() => {
        this._reportRepoService.updateFinalFlag(reportId, instanceId, true, true).subscribe(() => {
          this.getReportInstances(reportId);
        });
      })
    );
  }

  getSignedDownloadURL(filePath: string, fileName: string, version: number, suffix: string): void {
    const { containerId } = this.workingContainer;
    let downloadFileSuffix = '';
    if (this.isMasterFile) {
      downloadFileSuffix = ReportFileTypesConstants.MASTER_FILE_REPORT;
    } else if (suffix) {
      downloadFileSuffix = suffix.includes(ReportFileTypesConstants.ANALYSIS_BENCHMARK_REPORT) ? ReportFileTypesConstants.BENCHMARK_REPORT : ReportFileTypesConstants.LOCAL_FILE_REPORT;
    }
    const downloadFileName = `${fileName} ${downloadFileSuffix} V${version}`;
    this.subscriptions.push(
      this._tpSafeService.getSignedDownloadURL(filePath, downloadFileName, containerId).subscribe(({ data: signedUrlInfo }) => {
        this.downloadFile(signedUrlInfo.url);
      })
    );
  }

  downloadFile(signedUrl: string): void {
    window.open(signedUrl, '_self');
  }

  hasReportFailed(reportInstance: ReportInstance): boolean {
    const status = reportInstance.status;
    const hasFailed = (status === ReportGenerationConstants.REPORT_STATUS_BY_NAME.FAILED);
    return hasFailed;
  }

  checkForFinalReport(reportInstances: ReportInstance[]): void {
    this.hasFinalVersion = reportInstances.some(report => !!report.finalVersionFlag);
  }

}
